/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, ReactElement } from "react";
import { Badge } from '@chakra-ui/react'
import axios from "axios";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
} from "@chakra-ui/modal";
import Card from "components/card";
import { Button } from '@chakra-ui/react';
import { TokenManager } from "utils/tokenManager";

const RequestWhitelistAddressBlacklistConfirmModal: React.FC<{
    dataToBlacklist: any;
    isOpen: boolean;
    onClose(): void;
    triggerReload(): void;
    setSwalProps: any;
}> = ({ isOpen, onClose, triggerReload, dataToBlacklist, setSwalProps }) => {

    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [reason, setReason] = useState<string>("");
    const userRole = localStorage.getItem("userRole");

    const closeModal = async () => {
        setReason("");
        onClose();
    };

    interface WhitelistAddressestatusItems {
        status: string;
        value: string;
        display: ReactElement;
    }

    const statusClasses = 'status';

    const statusList: WhitelistAddressestatusItems[] = [
        { status: "pending", value: 'pending', display: <Badge className={`${statusClasses} status-pending`}>Pending</Badge> },
        { status: "approved", value: 'approved', display: <Badge className={`${statusClasses} status-approved`}>Approved</Badge> },
        { status: "rejected", value: 'rejected', display: <Badge className={`${statusClasses} status-rejected`}>Rejected</Badge> },
        { status: "cancelled", value: 'cancelled', display: <Badge className={`${statusClasses} status-cancelled`}>Cancelled</Badge> }
    ];

    const handleSubmit = async () => {
        if (!reason.trim()) {
            setSwalProps({
                show: true,
                icon: "warning",
                title: "Validation Error",
                html: "Please provide a reason for blacklist.",
                showConfirmButton: true,
                didClose: () => {
                    setSwalProps({});
                },
            });
            return;
        }

        try {
            setIsProcessing(true);

            const data = {
                username: localStorage.getItem("username"),
                userRole: userRole,
                subaccount_id: dataToBlacklist.subaccount.id,
                wlad_id: dataToBlacklist.wlad_id,
                address: dataToBlacklist.address,
                subaccountName: dataToBlacklist.subaccount.name,
                network: dataToBlacklist.network,
                asset: dataToBlacklist.asset,
                status: "4",
                reason: reason.trim()
            }

            await axios.post(process.env.REACT_APP_API_URL + `/api/${userRole.toLowerCase()}/trade/action/update-whitelist-address-status`, data)
                .then(function (response) {
                    if (response && response.data?.status === 'ok') {
                        closeModal();
                        setSwalProps({
                            show: true,
                            icon: "success",
                            title: "Success",
                            html: "Whitelist address has been set to blacklist",
                            showConfirmButton: true,
                            didClose: () => {
                                triggerReload();
                                setSwalProps({});
                                setIsProcessing(false);
                            },
                        });
                    } else {
                        setIsProcessing(false);
                    }
                })
                .catch(function (error) {
                    setIsProcessing(false);
                    setSwalProps({
                        show: true,
                        icon: "error",
                        title: "Oops!",
                        html: error.response.data.message,
                        showConfirmButton: true,
                        didClose: () => {
                            setSwalProps({});
                            setIsProcessing(false);
                        },
                    });
                });
        } catch (error: any) {
            setIsProcessing(false);
            setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: "Something went wrong, please try again later.",
                showConfirmButton: true,
                didClose: () => {
                    setSwalProps({});
                    setIsProcessing(false);
                },
            });
        }
    };

    const formatStatus = (status: string) => {
        const statusItem = statusList.find(item => item.status === status);
        return statusItem;
    };

    const isFormValid = reason.trim() !== "";

    return (
        <>
            {dataToBlacklist &&
                <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
                    <ModalOverlay className="bg-[#000] !opacity-30" />
                    <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-2xl !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[12vh]">
                        <ModalBody className="p-6 md:p-7">
                            <div className="mb-4 flex items-start gap-3">
                                <div className="rounded-xl bg-red-900 p-2 text-red-100 ring-1 ring-red-800 dark:bg-red-950/40 dark:text-red-200 dark:ring-red-900/50">
                                    <svg
                                        viewBox="0 0 24 24"
                                        className="h-5 w-5"
                                        fill="currentColor"
                                    >
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 11c-.55 0-1-.45-1-1V8c0-.55.45-1 1-1s1 .45 1 1v4c0 .55-.45 1-1 1zm1 4h-2v-2h2v2z" />
                                    </svg>
                                </div>
                                <div>
                                    <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        Blacklist Whitelist Address
                                    </h1>
                                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                                        This action cannot be undone
                                    </p>
                                </div>
                            </div>

                            <div className="mb-4 rounded-xl border-2 border-red-900 bg-red-950/10 px-4 py-3 text-sm text-red-900 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200">
                                <span className="font-semibold">Warning:</span> You're about to blacklist this address. This action cannot be undone and will permanently block this address.
                            </div>

                            <div className="dark:bg-neutral-800/50 mb-6 rounded-xl bg-gray-50 p-4 ring-1 ring-gray-100 dark:ring-white/10">
                                <div className="flex flex-col gap-3 text-sm">
                                    <div className="flex">
                                        <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                                            Nickname
                                        </div>
                                        <div className="font-medium text-gray-900 dark:text-white">
                                            {dataToBlacklist.nickname}
                                        </div>
                                    </div>

                                    <div className="flex">
                                        <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                                            Address
                                        </div>
                                        <div className="break-all font-medium text-gray-900 dark:text-white">
                                            {dataToBlacklist.address}
                                        </div>
                                    </div>

                                    <div className="flex">
                                        <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                                            Asset
                                        </div>
                                        <div className="font-medium text-gray-900 dark:text-white">
                                            {dataToBlacklist.asset}
                                        </div>
                                    </div>

                                    <div className="flex">
                                        <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                                            Network
                                        </div>
                                        <div className="font-medium text-gray-900 dark:text-white">
                                            {dataToBlacklist.network}
                                        </div>
                                    </div>

                                    <div className="flex">
                                        <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                                            Merchant
                                        </div>
                                        <div className="font-medium text-gray-900 dark:text-white">
                                            {dataToBlacklist.merchant.name}
                                        </div>
                                    </div>

                                    <div className="flex">
                                        <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                                            Subaccount
                                        </div>
                                        <div className="font-medium text-gray-900 dark:text-white">
                                            {dataToBlacklist.subaccount.name}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-5">
                                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
                                    Reason <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    disabled={isProcessing}
                                    className="dark:bg-neutral-900 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none ring-red-900/0 placeholder:text-gray-400 focus:border-red-900 focus:ring-2 focus:ring-red-900/20 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:text-white h-28 resize-none"
                                    id="reason"
                                    autoComplete="off"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="Enter reason for blacklisting"
                                />
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    Provide a detailed reason for blacklisting this address.
                                </p>
                            </div>

                            <div className="flex justify-end gap-2">
                                <Button
                                    onClick={() => closeModal()}
                                    isDisabled={isProcessing}
                                    className="dark:bg-neutral-900 dark:hover:bg-neutral-800 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-white/10 dark:text-white"
                                >
                                    Not now
                                </Button>

                                <Button
                                    isDisabled={!isFormValid || isProcessing || TokenManager.isImpersonating()}
                                    onClick={() => handleSubmit()}
                                    isLoading={isProcessing}
                                    loadingText='Processing...'
                                    className="rounded-lg bg-red-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-red-900/50 disabled:cursor-not-allowed disabled:bg-gray-400"
                                >
                                    Process blacklist
                                </Button>
                            </div>
                        </ModalBody>
                    </ModalContent>
                </Modal>
            }
        </>
    );
};

export default RequestWhitelistAddressBlacklistConfirmModal;